import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Map, List } from 'immutable';

import { AppPage } from '../AppPage';
import Button from '../helpers/Button';
import { EProjectDetails } from './EProjectDetails';
import { UniverseTable } from './UniverseTable';

import style from './style.module.css';

const PROJECT_BACKGROUND = '#d7c4c4';

/**
 * Project component.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project data.
 * @param props.users {Immutable.List} User list.
 * @param props.harvcoTags {Immutable.List} Harvco tag list.
 * @param props.universe {Immutable.List} Target list.
 * @param props.common {Immutable.Map} Common object.
 * @param props.universeColumns {Immutable.List} Target column configurations.
 * @param props.savedDetails {Object} Saved details shown in the widget while the edit popup is open.
 * @param props.onRowDoubleClick {Function} Handle event of double clicking on a row.
 * @param props.onSort {Function} Handle event of sorting table.
 * @param props.onReload {Function} Handle event of reloading page.
 * @param props.onEdit {Function} Handle event of opening the project details popup.
 */
const EProjectComponent = ({
  projectData,
  users,
  harvcoTags,
  universe,
  common,
  universeColumns,
  savedDetails,
  onRowDoubleClick,
  onSort,
  onReload,
  onEdit,
  onTableRef,
  onCeoProjectUniverseChange,
}) => {
  const details = savedDetails || { projectData, users, harvcoTags, common };
  const toolbar = (
    <div className={style.toolbar}>
      <Button onClick={onReload}>
        <i aria-hidden="true" className="fa fa-refresh" /> Reload
      </Button>
    </div>
  );

  return (
    <div className={style.host}>
      <AppPage
        background={PROJECT_BACKGROUND}
        overline="Executive Project"
        title={details.projectData.getIn(['category', 'name']) || 'Executive Project'}
        toolbar={toolbar}
      >
        <EProjectDetails
          common={details.common}
          harvcoTags={details.harvcoTags}
          onEdit={onEdit}
          projectData={details.projectData}
          users={details.users}
        />
        <UniverseTable
          common={common}
          data={universe}
          onCeoProjectUniverseChange={onCeoProjectUniverseChange}
          onDoubleClick={onRowDoubleClick}
          onSort={onSort}
          onTableRef={onTableRef}
          projectData={details.projectData}
          universeColumns={universeColumns}
        />
      </AppPage>
    </div>
  );
};

EProjectComponent.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  harvcoTags: PropTypes.instanceOf(List).isRequired,
  onEdit: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func,
  onSort: PropTypes.func.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  savedDetails: PropTypes.shape({
    common: PropTypes.instanceOf(Map).isRequired,
    harvcoTags: PropTypes.instanceOf(List).isRequired,
    projectData: PropTypes.instanceOf(Map).isRequired,
    users: PropTypes.instanceOf(List).isRequired,
  }),
  universe: PropTypes.instanceOf(List).isRequired,
  universeColumns: PropTypes.instanceOf(List).isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

export const EProject = memo(EProjectComponent);
